import { useLazyQuery } from '@apollo/client'
import ProductCategories from '@deltek/specification-client/core/Constants/productCategories'
import SingleLineEllipsis from '@deltek/specification-client/core/Feedbacks/SingleLineEllipsis'
import { NameValuePair } from '@deltek/specification-client/core/Models/NameValuePair'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import Checkbox from '@material-ui/core/Checkbox'
import Chip from '@material-ui/core/Chip'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import Autocomplete, {
    AutocompleteChangeReason,
} from '@material-ui/lab/Autocomplete'
import Skeleton from '@material-ui/lab/Skeleton'
import {
    CategoriesQueryByFamily,
    ProprietaryCategoriesByFamily,
} from 'api/graphQL/queries/category.queries'
import { ProprietaryProductCategoryResponse } from 'api/graphQL/types/proprietaryProductCategory.types'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ProductListingDetailFormField } from '../ListingDetailsPage'

const useStyles = makeStyles((theme: Theme) => ({
    container: { paddingTop: theme.spacing(2) },
    autocompleteRoot: {
        width: 244,
        borderRadius: 4,
    },
    autocompletePopupIndicator: {
        padding: 0,
    },
    chipLabel: {
        ...theme.typography.h4,
        fontWeight: 'normal',
        overflow: 'unset',
        textOverflow: 'unset',
        whiteSpace: 'normal',
        maxWidth: 160,
    },
    checkbox: { marginRight: theme.spacing(1) },
    autocompleteInputAdornment: {
        maxHeight: 72,
        overflowY: 'auto',
    },
    chipLabelWidth: {
        maxWidth: 140,
    },
    loader: {
        height: theme.spacing(4),
    },
}))
interface ProductFamily {
    id: string
    productFamilyCategories: { name: string }[]
}

const MASTER_ID = '941a93c0-630d-4462-b04e-e0abbca32477'
const getAutocompleteValues = (
    values: string[],
    allCategories: NameValuePair<string>[] = []
) => {
    const localValues = values.map(
        (eachValue) =>
            allCategories.filter(
                (eachOption) => eachOption.value === eachValue
            )?.[0] || {
                name: eachValue,
                value: eachValue,
            }
    )
    if (values.length && values.length === allCategories.length) {
        localValues.push({ name: 'All', value: 'all' })
    }

    return localValues
}

interface EditCategoriesProps {
    selectedValues: string[]
    valid?: boolean
    sectionNumber?: string
    sectionName?: string
    listingType?: string
    newListingRequest?: boolean
    handleFieldChange: (
        field: ProductListingDetailFormField,
        value: string[]
    ) => void
}

export const EditCategories: React.FC<EditCategoriesProps> = ({
    selectedValues,
    valid,
    sectionName,
    sectionNumber,
    listingType,
    newListingRequest,
    handleFieldChange,
}) => {
    const classes = useStyles()
    const [localValue, setLocalValue] = useState<string[]>(selectedValues)
    const [options, setOptions] = useState<NameValuePair<string>[]>([])
    const [allCategories, setAllCategories] = useState<NameValuePair<string>[]>(
        []
    )

    const [
        getCategories,
        { data: productFamilyData, loading: categoriesFamilyLoading },
    ] = useLazyQuery<{
        productFamily: ProductFamily
    }>(CategoriesQueryByFamily, {
        fetchPolicy: 'cache-and-network',
    })

    const [
        getProprietaryProductCategories,
        {
            data: proprietaryProductCategoryData,
            loading: proprietaryCategoriesFamilyLoading,
        },
    ] = useLazyQuery<ProprietaryProductCategoryResponse>(
        ProprietaryCategoriesByFamily,
        {
            fetchPolicy: 'no-cache',
        }
    )

    const masterId = processEnv.MASTER_ID || MASTER_ID

    const optionLabel = (option: NameValuePair<string>) => option.name

    const handleChange = useCallback(
        (
            _event: React.ChangeEvent<{}>,
            value: NameValuePair<string>[],
            reason: AutocompleteChangeReason
        ) => {
            const valueContainsAll = value.some((each) => each.value === 'all')
            const isSelectedAndAvailableCategoriesEqual =
                selectedValues.length &&
                selectedValues.length === allCategories.length
            const isAvailableAndSelectedCategoriesNotEqual =
                allCategories.length &&
                selectedValues.length !== allCategories.length

            if (reason === 'select-option' || reason === 'remove-option') {
                if (
                    isSelectedAndAvailableCategoriesEqual &&
                    !valueContainsAll
                ) {
                    setLocalValue([])
                    handleFieldChange('categories', [])
                } else if (valueContainsAll) {
                    if (isAvailableAndSelectedCategoriesNotEqual) {
                        setLocalValue(allCategories.map((each) => each.value))
                        handleFieldChange(
                            'categories',
                            allCategories.map((each) => each.value)
                        )
                    } else {
                        setLocalValue(
                            value
                                .filter(
                                    (eachFilter) => eachFilter.value !== 'all'
                                )
                                .map((eachMap) => eachMap.value)
                        )
                        handleFieldChange(
                            'categories',
                            value
                                .filter(
                                    (eachFilter) => eachFilter.value !== 'all'
                                )
                                .map((eachMap) => eachMap.value)
                        )
                    }
                } else {
                    setLocalValue(value.map((eachMap) => eachMap.value))
                    handleFieldChange(
                        'categories',
                        value.map((eachMap) => eachMap.value)
                    )
                }
            }
        },
        [selectedValues, allCategories, setLocalValue, handleFieldChange]
    )

    useEffect(() => {
        if (!!sectionName)
            getCategories({
                variables: {
                    masterId,
                    sectionName,
                },
            })
    }, [sectionName, listingType, masterId, getCategories])

    useEffect(() => {
        if (!!sectionNumber && listingType === ListingLevel.PremiumProprietary)
            getProprietaryProductCategories({
                variables: {
                    input: { familyId: sectionNumber },
                },
            })
    }, [sectionNumber, listingType, getProprietaryProductCategories])

    useEffect(() => {
        let availableCategories: NameValuePair<string>[] = []

        if (listingType === ListingLevel.PremiumProprietary) {
            availableCategories =
                proprietaryProductCategoryData?.getProprietaryCategoriesByFamily?.map(
                    ({ name }) => ({ name, value: name })
                ) ?? []
        }

        // Proprietary listings can exist in MasterSpec families so we use the family categories when proprietary
        // categories don't exist
        if (!availableCategories.length) {
            if (productFamilyData?.productFamily) {
                availableCategories =
                    productFamilyData.productFamily.productFamilyCategories?.map(
                        ({ name }) => ({ name, value: name })
                    ) ?? []
            } else if (productFamilyData?.productFamily === null) {
                //This is the default fallback scenario when there is no family category mapping present in the database
                // Using constant values of 59 product categories as default options specified in DSC
                availableCategories =
                    ProductCategories.map((name) => ({ name, value: name })) ??
                    []
            }
        }

        if (!availableCategories.length) return
        setOptions([{ name: 'All', value: 'all' }, ...availableCategories])
        setAllCategories([...availableCategories])
        if (newListingRequest)
            setLocalValue([...availableCategories.map(({ name }) => name)])
    }, [
        productFamilyData,
        proprietaryProductCategoryData,
        listingType,
        newListingRequest,
    ])

    const loading = useMemo(
        () => categoriesFamilyLoading || proprietaryCategoriesFamilyLoading,
        [categoriesFamilyLoading, proprietaryCategoriesFamilyLoading]
    )

    return !loading ? (
        <div className={classes.container}>
            <Autocomplete<NameValuePair<string>, true, true>
                multiple
                clearOnBlur
                id="categories"
                data-testid="dvCategories"
                limitTags={3}
                classes={{
                    root: classes.autocompleteRoot,
                    popupIndicator: classes.autocompletePopupIndicator,
                }}
                options={options}
                value={getAutocompleteValues(localValue, allCategories)}
                disableClearable
                disableCloseOnSelect
                getOptionSelected={(option, value) =>
                    option.value === value.value
                }
                getOptionLabel={optionLabel}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        variant="outlined"
                        name="categories"
                        label="Categories"
                        placeholder={
                            localValue.length === 0 ? 'Select Categories' : ''
                        }
                        error={!valid}
                        helperText={
                            !valid ? 'Must select at least one category.' : ''
                        }
                        InputProps={{
                            ...params.InputProps,
                            startAdornment:
                                localValue.length === 0 ? (
                                    params.InputProps.startAdornment
                                ) : (
                                    <div
                                        className={
                                            classes.autocompleteInputAdornment
                                        }
                                    >
                                        {params.InputProps.startAdornment}
                                    </div>
                                ),
                            endAdornment:
                                localValue.length === 0 ? (
                                    params.InputProps.endAdornment
                                ) : (
                                    <div
                                        className={
                                            classes.autocompleteInputAdornment
                                        }
                                    >
                                        {params.InputProps.endAdornment}
                                    </div>
                                ),
                        }}
                        autoComplete="off"
                    />
                )}
                renderTags={(value, getTagProps) => {
                    if (value.some((each) => each.value === 'all')) {
                        return (
                            <Chip
                                {...getTagProps({
                                    index: allCategories.length,
                                })}
                                className="dvCategoriesSelected"
                                classes={{ label: classes.chipLabel }}
                                variant="outlined"
                                size="small"
                                label={<SingleLineEllipsis text="All" />}
                            />
                        )
                    }
                    return value.map(
                        (option, index) =>
                            option.value !== 'all' && (
                                <Chip
                                    {...getTagProps({ index })}
                                    className="dvCategoriesSelected"
                                    classes={{
                                        label: clsx(
                                            classes.chipLabel,
                                            index === 2 &&
                                                classes.chipLabelWidth
                                        ),
                                    }}
                                    variant="outlined"
                                    size="small"
                                    label={
                                        <SingleLineEllipsis
                                            text={option.name}
                                        />
                                    }
                                />
                            )
                    )
                }}
                renderOption={(option, state) => {
                    const selectAllProps =
                        allCategories.length > 0 && option.value === 'all'
                            ? {
                                  checked:
                                      allCategories.length ===
                                      localValue.length,
                              }
                            : {}
                    return (
                        <>
                            <Checkbox
                                color="primary"
                                icon={
                                    <CheckBoxOutlineBlankIcon fontSize="small" />
                                }
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                className={classes.checkbox}
                                checked={state.selected}
                                {...selectAllProps}
                            />
                            <Typography component="span" variant="h5">
                                {optionLabel(option)}
                            </Typography>
                        </>
                    )
                }}
                onChange={handleChange}
            />
        </div>
    ) : (
        <Skeleton variant="rect" className={classes.loader} />
    )
}
